import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import Select from "react-select";
import ApiService from "../util/ApiService";
import Swal from "sweetalert2";

require("./assets/css/Video.css");

const validationSchema = Yup.object({
    name: Yup.string()
        .required('Nome é obrigatório')
        .min(2, 'Nome deve ter pelo menos 2 caracteres'),
    email: Yup.string()
        .email('Email inválido')
        .required('Email é obrigatório para enviar o convite'),
    phone: Yup.string(),
    document: Yup.string(),
    reason: Yup.string(),
    department_id: Yup.number().nullable()
});

const VideoCreateAttendanceModal = ({ show, onHide, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);

    useEffect(() => {
        if (show) {
            fetchDepartments();
        }
    }, [show]);

    const fetchDepartments = async () => {
        try {
            setLoadingDepartments(true);
            const response = await ApiService.send(
                "get",
                `${process.env.REACT_APP_URL_API}video/departments/list-department`,
                { per_page: 100, status: 1 },
                true
            );

            if (response.code === 200) {
                const { data } = ApiService.handleApiResponse(response);
                setDepartments(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Erro ao carregar departamentos:', error);
        } finally {
            setLoadingDepartments(false);
        }
    };

    const initialValues = {
        name: '',
        email: '',
        phone: '',
        document: '',
        reason: '',
        department_id: null,
        add_to_queue: true
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        try {
            setLoading(true);

            const payload = {
                client: {
                    name: values.name,
                    email: values.email,
                    phone: values.phone || null,
                    document: values.document || null
                },
                department_id: values.department_id,
                reason: values.reason || null,
                add_to_queue: values.add_to_queue
            };

            const response = await ApiService.send(
                "post",
                `${process.env.REACT_APP_URL_API}video/attendances/invite`,
                payload,
                true
            );

            if (response.code === 200 || response.code === 201) {
                const result = response.data;

                await Swal.fire({
                    title: 'Convite Enviado!',
                    html: `
                        <div class="text-start">
                            <p><strong>Protocolo:</strong> ${result.attendance?.protocol || 'N/A'}</p>
                            <p><strong>Email enviado para:</strong> ${result.email_sent_to}</p>
                            <p class="text-muted small">O cliente receberá um email com o link para acessar a chamada de vídeo.</p>
                            <hr>
                            <p class="mb-1"><strong>Link de acesso:</strong></p>
                            <div class="input-group">
                                <input type="text" class="form-control form-control-sm" value="${result.client_join_url}" id="copyLinkInput" readonly>
                                <button class="btn btn-outline-primary btn-sm" type="button" onclick="navigator.clipboard.writeText('${result.client_join_url}'); this.innerHTML='Copiado!';">
                                    <i class="fa fa-copy"></i> Copiar
                                </button>
                            </div>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonText: 'Fechar'
                });

                resetForm();
                onHide();
                if (onSuccess) onSuccess(result);
            }
        } catch (error) {
            ApiService.handleApiError(error);
        } finally {
            setLoading(false);
            setSubmitting(false);
        }
    };

    const departmentOptions = departments.map(d => ({
        value: d.id,
        label: d.name
    }));

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fa fa-video me-2"></i>
                    Criar Atendimento e Enviar Convite
                </Modal.Title>
            </Modal.Header>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ errors, touched, isSubmitting, values, setFieldValue }) => (
                    <Form>
                        <Modal.Body>
                            <div className="alert alert-info mb-4">
                                <i className="fa fa-info-circle me-2"></i>
                                Preencha os dados do cliente para criar um atendimento e enviar o link de acesso por email.
                            </div>

                            <h6 className="text-muted mb-3">Dados do Cliente</h6>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Nome *</label>
                                    <Field
                                        name="name"
                                        type="text"
                                        className={`form-control ${errors.name && touched.name ? 'is-invalid' : ''}`}
                                        placeholder="Nome completo do cliente"
                                    />
                                    {errors.name && touched.name && (
                                        <div className="invalid-feedback">{errors.name}</div>
                                    )}
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Email *</label>
                                    <Field
                                        name="email"
                                        type="email"
                                        className={`form-control ${errors.email && touched.email ? 'is-invalid' : ''}`}
                                        placeholder="email@exemplo.com"
                                    />
                                    {errors.email && touched.email && (
                                        <div className="invalid-feedback">{errors.email}</div>
                                    )}
                                    <small className="text-muted">O convite será enviado para este email</small>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Telefone</label>
                                    <Field
                                        name="phone"
                                        type="text"
                                        className="form-control"
                                        placeholder="(00) 00000-0000"
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">CPF/Documento</label>
                                    <Field
                                        name="document"
                                        type="text"
                                        className="form-control"
                                        placeholder="000.000.000-00"
                                    />
                                </div>
                            </div>

                            <hr className="my-4" />

                            <h6 className="text-muted mb-3">Detalhes do Atendimento</h6>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Departamento</label>
                                    <Select
                                        options={departmentOptions}
                                        value={departmentOptions.find(opt => opt.value === values.department_id)}
                                        onChange={(selected) => setFieldValue('department_id', selected?.value || null)}
                                        placeholder="Selecione um departamento"
                                        isClearable
                                        isLoading={loadingDepartments}
                                        noOptionsMessage={() => "Nenhum departamento disponível"}
                                    />
                                </div>

                                <div className="col-md-6 mb-3">
                                    <label className="form-label">Motivo/Assunto</label>
                                    <Field
                                        name="reason"
                                        type="text"
                                        className="form-control"
                                        placeholder="Ex: Consulta de rotina"
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                <div className="form-check">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="addToQueue"
                                        checked={values.add_to_queue}
                                        onChange={(e) => setFieldValue('add_to_queue', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="addToQueue">
                                        Adicionar à fila de espera automaticamente
                                    </label>
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            <button type="button" className="btn btn-secondary" onClick={onHide}>
                                <i className="fa fa-times me-2"></i>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-success" disabled={isSubmitting || loading}>
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa fa-paper-plane me-2"></i>
                                        Criar e Enviar Convite
                                    </>
                                )}
                            </button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default VideoCreateAttendanceModal;
